import React, { memo } from 'react';
import { ArrowDown, ArrowUp, ArrowUpDown, Archive, ArchiveRestore, Camera, Pencil, AlertTriangle } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';

const money = (v) => (v === null || v === undefined || v === '' ? '—' : `$${parseFloat(v || 0).toFixed(2)}`);
const qtyFmt = (n) => (Number.isInteger(n) ? n : Number(n || 0).toFixed(2));

const COLS = [
  { key: 'name', label: 'Item', sortable: true },
  { key: 'trade', label: 'Trade', sortable: true },
  { key: 'category', label: 'Category', sortable: true },
  { key: 'supplier', label: 'Preferred Supplier', sortable: true },
  { key: 'barcode', label: 'Barcode', sortable: true },
  { key: 'on_hand', label: 'Shop + WH On Hand', sortable: true, right: true, title: 'Exact controlled stock: every Shop and Warehouse location combined. Vans are NOT included.' },
  { key: 'cost', label: 'Cost', sortable: true, right: true },
  { key: 'sell', label: 'Sell Price', sortable: true, right: true },
  { key: 'actions', label: 'Actions', right: true },
];

const SortHeader = ({ col, sort, dir, onSort }) => {
  const active = sort === col.key;
  const Icon = !active ? ArrowUpDown : dir === 'asc' ? ArrowUp : ArrowDown;
  return (
    <button type="button" data-testid={`inv-sort-${col.key}`} onClick={() => onSort(col.key)} title={col.title}
      className={`inline-flex items-center gap-1 hover:text-white transition-colors ${active ? 'text-[#D4AF37]' : ''}`}>
      {col.label}<Icon className="w-3 h-3" />
    </button>
  );
};

const Row = memo(({ it, checked, onToggle, onOpen, onEdit, onArchive, onUnarchive, onPhoto }) => (
  <tr data-testid={`inv-item-row-${it.id}`} onClick={() => onOpen(it.id)}
    className={`group border-b border-[#262626] last:border-0 cursor-pointer hover:bg-[#1a1a1a] ${checked ? 'bg-[#1f1b10]' : ''}`}>
    <td className={`px-3 py-2 w-10 min-w-[40px] max-w-[40px] sticky left-0 z-10 ${checked ? 'bg-[#1f1b10]' : 'bg-[#141414]'} group-hover:bg-[#1a1a1a]`} onClick={(e) => e.stopPropagation()}>
      <input data-testid={`inv-item-select-${it.id}`} type="checkbox" checked={checked} onChange={() => onToggle(it.id)} className="accent-[#D4AF37]" />
    </td>
    <td className={`px-3 py-2 text-white sticky left-[40px] z-10 min-w-[200px] max-w-[280px] border-r border-[#262626] ${checked ? 'bg-[#1f1b10]' : 'bg-[#141414]'} group-hover:bg-[#1a1a1a]`}>
      <span data-testid={`inv-item-open-${it.id}`} className="flex items-center gap-2 hover:text-[#D4AF37] transition-colors">
        <span className="truncate">{it.name}</span>
        {it.archived && <Badge className="bg-amber-900/30 text-amber-400 border-amber-700 text-[10px]">ARCHIVED</Badge>}
      </span>
      <p className="text-[#525252] text-xs truncate">{[it.size, it.sku ? `SKU ${it.sku}` : '', it.brand].filter(Boolean).join(' · ') || '\u00a0'}</p>
    </td>
    <td className="px-3 py-2">
      {(it.departments || []).length
        ? <div className="flex flex-wrap gap-1">{it.departments.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded border border-[#D4AF37]/30 text-[#D4AF37]">{t}</span>)}</div>
        : <span className="text-[#525252] text-xs">No Trade</span>}
    </td>
    <td className="px-3 py-2 text-[#A3A3A3] whitespace-nowrap max-w-[170px] truncate" title={it.category || ''}>{it.category || '—'}</td>
    <td className="px-3 py-2 text-[#A3A3A3] whitespace-nowrap max-w-[150px] truncate" title={it.preferred_supplier || ''}>{it.preferred_supplier || 'Unassigned'}</td>
    <td className="px-3 py-2 text-[#A3A3A3] font-mono text-xs whitespace-nowrap">{it.barcode || '—'}</td>
    <td data-testid={`inv-item-onhand-${it.id}`} className="px-3 py-2 text-right whitespace-nowrap">
      <span className={it.low_stock ? 'text-amber-400 font-semibold' : it.controlled_on_hand > 0 ? 'text-white' : 'text-[#525252]'}>
        {it.low_stock && <AlertTriangle className="w-3 h-3 inline mr-1" />}{qtyFmt(it.controlled_on_hand || 0)}
      </span>
      {it.van_estimated > 0 && <span className="block text-[10px] text-[#737373]" title="Estimated On Van — rough reference from transfers in, not a physical count">~{qtyFmt(it.van_estimated)} estimated on vans</span>}
    </td>
    <td className="px-3 py-2 text-right text-white whitespace-nowrap">{money(it.default_cost)}</td>
    <td className="px-3 py-2 text-right text-[#D4AF37] font-medium whitespace-nowrap">{money(it.sell_price)}</td>
    <td className="px-3 py-2 text-right" onClick={(e) => e.stopPropagation()}>
      <div className="inline-flex gap-1">
        <label className="cursor-pointer">
          <input type="file" accept=".png,.jpg,.jpeg,.webp" className="hidden" onChange={(e) => onPhoto(it, e.target.files?.[0])} />
          <Button data-testid={`inv-item-photo-${it.id}`} asChild size="sm" variant="outline" className="border-[#262626] text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-2 pointer-events-none"><span><Camera className="w-4 h-4" /></span></Button>
        </label>
        <Button data-testid={`inv-item-edit-${it.id}`} onClick={() => onEdit(it)} size="sm" variant="outline" className="border-[#D4AF37]/40 text-[#D4AF37] hover:bg-[#D4AF37]/10 h-8 px-2"><Pencil className="w-4 h-4" /></Button>
        {it.archived
          ? <Button data-testid={`inv-item-unarchive-${it.id}`} onClick={() => onUnarchive(it)} size="sm" variant="outline" className="border-green-700/40 text-green-400 hover:bg-green-700/10 h-8 px-2"><ArchiveRestore className="w-4 h-4" /></Button>
          : <Button data-testid={`inv-item-archive-${it.id}`} onClick={() => onArchive(it)} size="sm" variant="outline" className="border-[#262626] text-amber-400 hover:bg-amber-900/20 h-8 px-2"><Archive className="w-4 h-4" /></Button>}
      </div>
    </td>
  </tr>
));

// Master Inventory grid: sticky header + sticky Item column, server-side sort/paging.
export const MasterInventoryTable = ({ items, sort, dir, onSort, selected, onToggle, allSelected, onToggleAll, loading, emptyText, ...rowHandlers }) => (
  <div data-testid="inv-items-table-scroll" className="bg-[#141414] border border-[#262626] rounded-md overflow-auto max-h-[calc(100vh-320px)] min-h-[320px]">
    <table className="w-full text-sm border-separate border-spacing-0">
      <thead data-testid="inv-items-thead" className="sticky top-0 z-20">
        <tr>
          <th className="sticky left-0 z-30 bg-[#0A0A0A] border-b border-[#262626] px-3 py-2 w-10 min-w-[40px] max-w-[40px] text-left">
            <input data-testid="inv-items-select-all" type="checkbox" checked={allSelected} onChange={onToggleAll} className="accent-[#D4AF37]" />
          </th>
          {COLS.map((c, i) => (
            <th key={c.key} className={`bg-[#0A0A0A] border-b border-[#262626] px-3 py-2 text-[#A3A3A3] font-medium whitespace-nowrap ${c.right ? 'text-right' : 'text-left'} ${i === 0 ? 'sticky left-[40px] z-30 border-r' : ''}`}>
              {c.sortable ? <SortHeader col={c} sort={sort} dir={dir} onSort={onSort} /> : c.label}
            </th>
          ))}
        </tr>
      </thead>
      <tbody className={loading ? 'opacity-60 transition-opacity' : 'transition-opacity'}>
        {items.length === 0 ? (
          <tr><td colSpan={COLS.length + 1} className="px-4 py-8 text-center text-[#525252]" data-testid="inv-items-empty">{loading ? 'Loading…' : emptyText}</td></tr>
        ) : items.map((it) => <Row key={it.id} it={it} checked={selected.has(it.id)} onToggle={onToggle} {...rowHandlers} />)}
      </tbody>
    </table>
  </div>
);

export default MasterInventoryTable;
